import { useEffect, useRef } from 'react';

import { IoCloseSharp } from 'react-icons/io5';
import { MdArrowForwardIos } from 'react-icons/md';

import { useSearch } from './contexts/SearchContext';
import { cn } from '~/utils/cnTailwind.ts';

interface SearchTagProps {
    fieldKey: string;
    value?: string | null;
    isEditing: boolean;
    isLastTag: boolean;
    inputRef: React.MutableRefObject<HTMLInputElement | null>;
}

const SearchTag: React.FC<SearchTagProps> = ({ fieldKey, inputRef, isEditing, isLastTag, value }) => {
    const editableTagRef = useRef<HTMLDivElement>(null);
    const { handleTagCancel, handleTagDelete, handleTagEdit, handleTagSave, lastTagHighlighted } = useSearch();

    useEffect(() => {
        if (isEditing && editableTagRef.current) {
            editableTagRef.current.focus();

            const range = document.createRange();
            const sel = window.getSelection();

            if (editableTagRef.current.childNodes.length === 0) {
                const textNode = document.createTextNode('');
                editableTagRef.current.appendChild(textNode);
            }

            const lastNode = editableTagRef.current.lastChild;

            if (lastNode) {
                range.setStart(lastNode, lastNode.textContent?.length || 0);
                range.collapse(true);

                if (sel) {
                    sel.removeAllRanges();
                    sel.addRange(range);
                }
            }
        }
    }, [isEditing]);

    if (value === null || value === '') {
        // This is a tag being edited
        return (
            <div
                data-testid="search-tag-edit"
                data-key={fieldKey}
                onClick={(e) => {
                    e.stopPropagation();
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        const fullText = (e.target as HTMLDivElement).textContent || '';
                        if (fullText.length > 0) {
                            handleTagSave(fieldKey, fullText, inputRef);
                        }
                    } else if (e.key === 'Escape') {
                        const isEmpty = !editableTagRef.current?.textContent;
                        handleTagCancel(fieldKey, isEmpty, inputRef);
                    }
                }}
                className={cn(
                    'inline-flex items-center bg-grey-light text-xs text-grey rounded-xl py-1 pr-2 pl-4 cursor-pointer mr-1 gap-1',
                    isLastTag && lastTagHighlighted ? 'bg-blue-200' : '',
                )}
            >
                <span>{fieldKey}</span>
                <MdArrowForwardIos />
                {/* we can't use an input element here because it doesn't grow with content (vanilla) and a normal input somehow triggers a password manager here??? */}
                <div
                    data-testid="search-tag-input"
                    ref={editableTagRef}
                    contentEditable={true}
                    className="tag-being-edited min-w-[30px] px-1 outline outline-1 outline-blue-light"
                ></div>
                <span
                    data-testid="search-tag-close-icon"
                    className="ml-1 text-grey hover:text-grey-dark cursor-pointer pr-1"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleTagDelete(fieldKey);
                    }}
                >
                    <IoCloseSharp />
                </span>
            </div>
        );
    }

    // This is a completed tag (not being edited)
    return (
        <div
            data-testid="search-tag"
            data-key={fieldKey}
            className={cn(
                'inline-flex items-center bg-grey-light text-xs text-grey rounded-xl py-1 pr-2 pl-4 cursor-pointer mr-1 gap-1',
                isLastTag && lastTagHighlighted ? 'bg-blue-200' : '',
            )}
            onClick={(e) => {
                e.stopPropagation();
                handleTagEdit(fieldKey);
            }}
        >
            <span>{fieldKey}</span>
            <MdArrowForwardIos />
            <span>{value}</span>
            <span
                data-testid="search-tag-close-icon"
                className="ml-1 text-grey hover:text-grey-dark cursor-pointer pr-1"
                onClick={(e) => {
                    e.stopPropagation();
                    handleTagDelete(fieldKey);
                }}
            >
                <IoCloseSharp />
            </span>
        </div>
    );
};

export default SearchTag;
